import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../shared/components/Layout';
import AlertMessages from '@/components/AlertMessages';
import getOrderHistory from '../../api/getOrderHistory';

type IndexItem = {
  id: number;
  orderCode: string;
  orderedAt: string;
  orderType: string;
  status: string;
  shipDate: string | null;
};

type SearchFilters = {
  orderNumber: string;
  orderType: string;
  status: string;
  startDate: string;
  endDate: string;
};

const Index: React.FC = () => {
  const navigate = useNavigate();
  const [alertMessageOpen, setAlertMessageOpen] = useState<boolean>(false);
  const [alertMessages, setAlertMessages] = useState<string[]>([]);
  const [orderHistory, setOrderHistory] = useState<IndexItem[]>([]);
  const [filteredOrderHistory, setFilteredOrderHistory] = useState<IndexItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    orderNumber: '',
    orderType: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const fetchOrderHistoryData = async () => {
      try {
        const response = await getOrderHistory();
        const orders = response.data.orders || [];
        
        setOrderHistory(orders);
        setFilteredOrderHistory(orders);
        setLoading(false);
      } catch (error) {
        console.error('注文履歴の取得に失敗しました:', error);
        setAlertMessages(['注文履歴の取得に失敗しました。']);
        setAlertMessageOpen(true);
        setLoading(false);
      }
    };

    fetchOrderHistoryData();
  }, []);

  const handleSearch = () => {
    let filtered = orderHistory;

    if (searchFilters.orderNumber.trim()) {
      filtered = filtered.filter(order => 
        order.orderCode.toLowerCase().includes(searchFilters.orderNumber.toLowerCase())
      );
    }

    if (searchFilters.orderType) {
      filtered = filtered.filter(order => order.orderType === searchFilters.orderType);
    }

    if (searchFilters.status) {
      filtered = filtered.filter(order => order.status === searchFilters.status);
    }

    if (searchFilters.startDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderedAt);
        const startDate = new Date(searchFilters.startDate);
        return orderDate >= startDate;
      });
    }

    if (searchFilters.endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderedAt);
        const endDate = new Date(searchFilters.endDate);
        // 終了日の23:59:59まで含める
        endDate.setHours(23, 59, 59, 999);
        return orderDate <= endDate;
      });
    }

    setFilteredOrderHistory(filtered);
  };

  const handleClear = () => {
    setSearchFilters({
      orderNumber: '',
      orderType: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setFilteredOrderHistory(orderHistory);
  };

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'shipped':
        return 'badge-success';
      case 'preparing_for_shipment':
        return 'badge-warning';
      case 'received':
        return 'badge-info';
      case 'canceled':
        return 'badge-danger';
      default:
        return 'badge-default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'shipped':
        return '発送済み';
      case 'preparing_for_shipment':
        return '発送準備中';
      case 'received':
        return '注文受付';
      case 'canceled':
        return 'キャンセル';
      default:
        return status;
    }
  };

  const getOrderTypeLabel = (orderType: string) => {
    switch (orderType) {
      case 'trial':
        return 'トライアルレンズ';
      case 'commercial':
        return '製品レンズ';
      case 'flat_rate':
        return '定額制';
      case 'facility':
        return '施設定額制';
      case 'promo_care':
        return 'ケア用品';
      default:
        return orderType;
    }
  };

  return (
    <React.Fragment>
      <Layout>
        <main id="page-order-history" className="wrapper">
          <section className="sec-order-history sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01  mbsp-18">注文履歴</h1>
              
              {alertMessageOpen && (
                <AlertMessages
                  open={alertMessageOpen}
                  messages={alertMessages}
                />
              )}

              <div className="search-form">
                <div className="search-row">
                  <div className="search-field">
                    <label htmlFor="orderNumber">注文番号</label>
                    <input
                      type="text"
                      id="orderNumber"
                      value={searchFilters.orderNumber}
                      onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                      placeholder="注文番号を入力"
                    />
                  </div>
                  
                  <div className="search-field">
                    <label htmlFor="orderType">注文種別</label>
                    <select
                      id="orderType"
                      value={searchFilters.orderType}
                      onChange={(e) => handleFilterChange('orderType', e.target.value)}
                    >
                      <option value="">すべて</option>
                      <option value="trial">トライアルレンズ</option>
                      <option value="commercial">製品レンズ</option>
                      {/* TODO: 定額制と施設定額制が開始されたら表示 */}
                      {/* <option value="flat_rate">定額制</option>
                      <option value="facility">施設定額制</option> */}
                      <option value="promo_care">ケア用品</option>
                    </select>
                  </div>

									{/* TODO: ステータス絞り込みを開始したら表示 */}
                  {/*h<div className="search-field">
                    <label htmlFor="status">ステータス</label>
                    <select
                      id="status"
                      value={searchFilters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">すべて</option>
                      <option value="shipped">発送済み</option>
                      <option value="preparing_for_shipment">発送準備中</option>
                      <option value="received">注文受付</option>
                      <option value="canceled">キャンセル</option>
                    </select>
                  </div>*/}
                </div>

                <div className="search-row">
                  <div className="search-field">
                    <label htmlFor="startDate">注文日（開始）</label>
                    <input
                      type="date"
                      id="startDate"
                      value={searchFilters.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    />
                  </div>

                  <div className="search-field">
                    <label htmlFor="endDate">注文日（終了）</label>
                    <input
                      type="date"
                      id="endDate"
                      value={searchFilters.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    />
                  </div>

                  <div className="search-buttons">
                    <button 
                      type="button" 
                      className="btn-search"
                      onClick={handleSearch}
                    >
                      検索
                    </button>
                    <button 
                      type="button" 
                      className="btn-clear"
                      onClick={handleClear}
                    >
                      クリア
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="loading">読み込み中...</div>
              ) : (
                <div className="order-history-list">
                  {filteredOrderHistory.length === 0 ? (
                    <div className="no-orders">
                      <p>検索条件に一致する注文履歴がありません。</p>
                    </div>
                  ) : (
                    <table className="order-table">
                      <thead>
                        <tr>
                          <th>注文番号</th>
                          <th>注文日</th>
                          <th>注文種別</th>
                          {/* TODO: ステータス絞り込みを開始したら表示 */}
                          {/* <th>ステータス</th> */}
                          <th>出荷日</th>
                          <th>詳細</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrderHistory.map((order) => (
                          <tr key={order.id}>
                            <td>{order.orderCode}</td>
                            <td>{order.orderedAt}</td>
                            <td>{getOrderTypeLabel(order.orderType)}</td>
                            {/* TODO: ステータス絞り込みを開始したら表示 */}
                            {/* <td>
                              <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                                {getStatusLabel(order.status)}
                              </span>
                            </td> */}
                            <td>{order.shipDate || '-'}</td>
                            <td>
                              <button 
                                className="btn-detail"
                                onClick={() => {
                                  navigate(`/facility/orders/history/${order.id}`);
                                }}
                              >
                                詳細
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  );
};

export default Index;